import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"

import { deserializeEditorHTML } from "@/tiptap/core/editor-serialization"
import { lowlight } from "@/tiptap/extensions/html-preservation-extensions"

function editorIsEmpty(editor) {
  return typeof editor?.isEmpty === "function" ? editor.isEmpty() : !!editor?.isEmpty
}

const SOURCE_MEDIA_RE = /<(img|figure|iframe|video|audio|hr)\b/i

function getSourceFromEditor(editor) {
  const html = editor?.getHTML?.() || ""
  // Captionless figures/images have no text, so TipTap reports isEmpty even
  // though getHTML still has the media markup.
  if (editorIsEmpty(editor) && !SOURCE_MEDIA_RE.test(html)) return ""
  return beautifyHtml(html)
}

export function SourceModeModal({ editor, isOpen, onClose }) {
  const [source, setSource] = useState("")
  const textareaRef = useRef(null)
  const contentRef = useRef(null)
  const highlightRef = useRef(null)
  const lineNumbersRef = useRef(null)
  const mouseDownInsideModalRef = useRef(false)
  const highlightedSource = useMemo(() => highlightHtml(source), [source])
  const lineNumbers = useMemo(() => {
    return source.split("\n").map((_, index) => index + 1)
  }, [source])

  useEffect(() => {
    if (isOpen && editor) {
      setSource(getSourceFromEditor(editor))
      requestAnimationFrame(() => textareaRef.current?.focus())
    }
  }, [isOpen, editor])

  useEffect(() => {
    if (!isOpen) return

    const acquiredLock = !document.body.classList.contains("scroll-lock")
    const scrollPosition = window.scrollY

    if (acquiredLock) {
      document.body.style.top = `-${scrollPosition}px`
      document.body.classList.add("scroll-lock")
    }

    const handleEscape = (event) => {
      if (event.key !== "Escape") return

      event.preventDefault()
      event.stopPropagation()
      onClose()
    }

    document.addEventListener("keydown", handleEscape)
    return () => {
      document.removeEventListener("keydown", handleEscape)
      if (!acquiredLock) return

      document.body.classList.remove("scroll-lock")
      document.body.style.top = ""
      window.scrollTo(0, scrollPosition)
    }
  }, [isOpen, onClose])

  const handleSave = useCallback(() => {
    if (editor) {
      editor.commands.setContent(deserializeEditorHTML(source))
    }
    onClose()
  }, [editor, source, onClose])

  const handleMouseDown = useCallback((event) => {
    mouseDownInsideModalRef.current = contentRef.current?.contains(event.target) ?? false
  }, [])

  const handleBackdropClick = useCallback(
    (event) => {
      const clickedOutside =
        event.target === event.currentTarget &&
        !mouseDownInsideModalRef.current &&
        !contentRef.current?.contains(event.target)

      if (clickedOutside) onClose()
      mouseDownInsideModalRef.current = false
    },
    [onClose],
  )

  const handleTextareaScroll = useCallback((event) => {
    if (highlightRef.current) {
      highlightRef.current.scrollTop = event.currentTarget.scrollTop
      highlightRef.current.scrollLeft = event.currentTarget.scrollLeft
    }

    if (lineNumbersRef.current) {
      lineNumbersRef.current.scrollTop = event.currentTarget.scrollTop
    }
  }, [])

  if (!isOpen) return null

  const dialog = (
    <div
      className="manage_modal__wrapper"
      onMouseDown={handleMouseDown}
      onClick={handleBackdropClick}
    >
      <div
        ref={contentRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="tiptap-source-dialog-title"
        className="manage_modal manage_modal--with-footer manage_modal--large manage_modal--source-code"
        tabIndex={-1}
      >
        <header>
          <h1 id="tiptap-source-dialog-title">Source Code</h1>
          <button
            type="button"
            aria-label="Close"
            className="button button--icon button--unfilled"
            onClick={onClose}
          >
            <i className="far fa-times" />
          </button>
        </header>
        <div className="manage__modal__body">
          <div className="tiptap-source-dialog__editor">
            <div
              ref={lineNumbersRef}
              className="tiptap-source-dialog__line-numbers"
              aria-hidden="true"
            >
              {lineNumbers.map((lineNumber) => (
                <div key={lineNumber}>{lineNumber}</div>
              ))}
            </div>
            <div className="tiptap-source-dialog__code">
              <pre
                ref={highlightRef}
                className="tiptap-source-dialog__highlight"
                aria-hidden="true"
                dangerouslySetInnerHTML={{ __html: highlightedSource }}
              />
              <textarea
                ref={textareaRef}
                className="tiptap-source-dialog__textarea"
                value={source}
                onChange={(e) => setSource(e.target.value)}
                onScroll={handleTextareaScroll}
                spellCheck={false}
              />
            </div>
          </div>
        </div>
        <div className="manage_modal__footer">
          <div className="button-group">
            <button
              type="button"
              className="button button--secondary"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="button button--primary"
              onClick={handleSave}
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  )

  return typeof document === "undefined" ? dialog : createPortal(dialog, document.body)
}

const SELF_CLOSING_TAGS = new Set([
  "br", "hr", "img", "input", "meta", "link",
  "area", "base", "col", "embed", "source", "track", "wbr",
])

const PRESERVE_WHITESPACE_TAGS = new Set(["pre", "textarea"])

function tagPreservesWhitespace(tag, tagName) {
  if (PRESERVE_WHITESPACE_TAGS.has(tagName)) return true

  return /white-space\s*:\s*pre/i.test(tag)
}

export function beautifyHtml(html) {
  const tab = "  "
  let result = ""
  let indent = 0
  let i = 0
  let preserveTagName = null
  let preserveDepth = 0

  while (i < html.length) {
    if (html[i] === "<") {
      const tagEnd = html.indexOf(">", i)
      if (tagEnd === -1) break

      const tag = html.slice(i, tagEnd + 1)
      const isClosing = tag.startsWith("</")
      const tagNameMatch = tag.match(/<\/?(\w+)/)
      const tagName = tagNameMatch?.[1]?.toLowerCase()
      const isSelfClosing =
        tag.endsWith("/>") ||
        (tagName && SELF_CLOSING_TAGS.has(tagName))

      if (preserveTagName) {
        result += tag
        if (tagName === preserveTagName) {
          if (isClosing) {
            preserveDepth -= 1
            if (preserveDepth === 0) {
              preserveTagName = null
              result += "\n"
            }
          } else if (!isSelfClosing) {
            preserveDepth += 1
          }
        }
        i = tagEnd + 1
        continue
      }

      if (isClosing) indent = Math.max(0, indent - 1)

      result += tab.repeat(indent) + tag

      if (!isClosing && !isSelfClosing && tagPreservesWhitespace(tag, tagName)) {
        preserveTagName = tagName
        preserveDepth = 1
      } else {
        result += "\n"
        if (!isClosing && !isSelfClosing) indent++
      }

      i = tagEnd + 1
    } else {
      const nextTag = html.indexOf("<", i)
      const raw = html.slice(i, nextTag === -1 ? html.length : nextTag)
      if (preserveTagName) {
        result += raw
      } else {
        const text = raw.trim()
        if (text) result += tab.repeat(indent) + text + "\n"
      }
      i = nextTag === -1 ? html.length : nextTag
    }
  }

  return result.trimEnd()
}

function escapeHtml(value) {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;")
}

function highlightHtml(html) {
  try {
    return renderLowlightNodes(lowlight.highlight("xml", html).children)
  } catch (_error) {
    return escapeHtml(html)
  }
}

function renderLowlightNodes(nodes = []) {
  return nodes.map(renderLowlightNode).join("")
}

function renderLowlightNode(node) {
  if (node.type === "text") return escapeHtml(node.value || "")

  const className = Array.isArray(node.properties?.className)
    ? node.properties.className.join(" ")
    : ""
  const content = renderLowlightNodes(node.children || [])

  return className ? `<span class="${escapeHtml(className)}">${content}</span>` : content
}

export default SourceModeModal
